"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import KpResult, { type KpData } from "@/components/kp/KpResult";
import { generateKundali } from "@/lib/api/kundali";
import { safeSessionSet } from "@/lib/browser-storage";
import type { KundaliRequest } from "@ishvaram/api-client";

export default function KpAstrologyClient({ language = "en" }: { language?: "en" | "hi" }) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kpData, setKpData] = useState<KpData | null>(null);
  const [readingId, setReadingId] = useState<string | null>(null);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    setKpData(null);

    try {
      const result = await generateKundali({ ...data, skip_interpretation: true });
      const kp = result.kp as KpData | undefined;
      if (!kp || kp.system !== "kp") {
        setError("KP data not available for this birth chart. Please try again.");
        return;
      }
      setKpData(kp);
      setReadingId(result.id);
      persistChatIntent();
      router.push(`/chat/${result.id}?lang=${language}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setKpData(null);
    setReadingId(null);
    setError(null);
  }

  return (
    <div className="card-sacred p-6 sm:p-8 mb-6">
      {/* Section header */}
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-2 text-ish-accent">
          Free KP Calculator
        </p>
        <h2 className="text-[22px] sm:text-[26px] font-bold leading-tight text-ish-dark font-serif">
          Your KP Sub-Lord Analysis
        </h2>
        <p className="text-[14px] mt-1 text-ish-text-muted">
          Enter your birth details to get the full four-level lord chart — cuspal sub-lords,
          planetary KP positions, and key readings for marriage, career, health, and wealth.
        </p>
        <p className="text-[12px] mt-2 text-ish-text-faint">
          Exact birth time required — KP cuspal sub-lords shift every ~4 minutes.
        </p>
      </div>

      {/* Form (shown until result arrives) */}
      {!kpData && (
        <div className="max-w-md">
          <BirthDetailsForm
            rememberBirthDetails
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel="Calculate KP Chart"
            error={error ?? undefined}
            requireBirthTime={true}
            analyticsToolName="kp-astrology"
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-10">
          <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto border-ish-accent" />
          <p className="mt-4 text-[14px] text-ish-text-muted">
            Computing KP chart via Swiss Ephemeris...
          </p>
          <p className="mt-1 text-[12px] text-ish-text-faint">
            Applying Krishnamurti ayanamsa + Placidus houses
          </p>
        </div>
      )}

      {/* Results */}
      {kpData && !loading && (
        <div className="space-y-6">
          <KpResult kp={kpData} />

          {/* AI Astrologer CTA */}
          {readingId && (
            <AIAstrologerCTA
              context="kundli"
              headline="Interpret your KP chart with the AI Astrologer"
              subhead="Ask about marriage timing, career dasha, or any life question — free and instant."
            />
          )}

          {/* Reset */}
          <div className="text-center">
            <button
              onClick={handleReset}
              className="text-[13px] font-semibold text-ish-text-muted border border-ish-border rounded-lg px-6 py-2.5 hover:border-ish-accent hover:text-ish-accent transition-colors"
            >
              Calculate for Another Person
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function persistChatIntent() {
  const sourcePath = typeof window === "undefined" ? "/kp-astrology" : window.location.pathname;
  safeSessionSet("seo_entry_intent", "chart_report");
  safeSessionSet("seo_entry_path", sourcePath || "/kp-astrology");
  safeSessionSet("seo_entry_page_category", "kundali");
  safeSessionSet("seo_entry_primary_offer", "report");
  safeSessionSet("seo_entry_report_plan_id", "brihat_kundali");
}
